import { User as UserIcon, LayoutGrid, Award, LogOut, ShieldCheck, Key, UserCircle, Menu, X, Sun, Moon, Map } from 'lucide-react';
import styles from './student-nav.module.css';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Modal, Form, Button, Alert, NavDropdown } from 'react-bootstrap';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import api, { API_ORIGIN } from '../../services/api';
import React, { useState } from 'react';

interface StudentNavProps {
  studentName?: string;
  studentAvatar?: string;
}

export function StudentNav({ studentName = 'User', studentAvatar = 'AH' }: StudentNavProps) {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '', loading: false });
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    window.location.reload();
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({ type: 'danger', message: 'New passwords do not match', loading: false });
      return;
    }
    setPasswordStatus({ type: '', message: '', loading: true });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ORIGIN}/api/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to change password');
      
      setPasswordStatus({ type: 'success', message: 'Password changed successfully!', loading: false });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordStatus({ type: '', message: '', loading: false });
      }, 2000);
    } catch (err: any) {
      setPasswordStatus({ type: 'danger', message: err.message, loading: false });
    }
  };

  return (
    <header className={styles.nav}>
      <div className={styles.inner}>
        {/* Left: Branding */}
        <Link to="/user/home" className={styles.brandWrapper}>
          <img src="/logo.png" alt="DataPylot Logo" className={styles.logoImage} />
          <span className={styles.logoText}>DataPylot</span>
        </Link>

        <button
          type="button"
          className={styles.mobileToggle}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMobileOpen(!mobileOpen);
          }}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        {/* Middle: Navigation Links */}
        <nav className={styles.links}>
          <NavLink to="/user/home" className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}>
            Home
          </NavLink>

          <NavLink to="/user" className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`} end>
            My Learning
          </NavLink>
          <NavLink to="/user/certificates" className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}>
            Certificates
          </NavLink>
          <NavLink to="/playground" className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}>
            Playground
          </NavLink>
          <NavLink to="/user/career-paths" className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}>
            Career Paths
          </NavLink>
        </nav>

        {/* Right: Actions & Profile */}
        <div className={styles.actions}>
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="btn btn-link p-0 border-0 shadow-none theme-toggle-btn"
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            style={{ color: 'var(--color-text)' }}
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </button>

           <NavDropdown 
             title={
                <div className="d-flex align-items-center gap-2 px-1">
                  <UserCircle size={20} className={styles.navIcon} />
                  <span className="fw-bold text-uppercase" style={{ fontSize: '13px', letterSpacing: '0.8px', color: 'var(--color-text)' }}>
                    {user?.username?.split(' ')[0] || 'PROFILE'}
                  </span>
                  <span className="material-symbols-outlined text-muted text-sm ms-1">expand_more</span>
                </div>
             }
             id="student-nav-dropdown"
             align="end"
             className={styles.profileDropdown}
           >
              <NavDropdown.Header className="fw-bold px-3 py-2" style={{ color: 'var(--color-text)' }}>
                Hello, {user?.username}
              </NavDropdown.Header>
              
              <LinkContainer to="/profile">
                <NavDropdown.Item className="d-flex align-items-center gap-2 py-2">
                  <UserCircle size={18} className="text-muted" />
                  My Profile
                </NavDropdown.Item>
              </LinkContainer>

              <NavDropdown.Item 
                className="d-flex align-items-center gap-2 py-2"
                onClick={() => setShowPasswordModal(true)}
              >
                <Key size={18} className="text-muted" />
                Change Password
              </NavDropdown.Item>

              <NavDropdown.Divider />
              
              <NavDropdown.Item 
                className="d-flex align-items-center gap-2 py-2 text-danger"
                onClick={handleLogout}
              >
                <LogOut size={18} />
                Sign Out
              </NavDropdown.Item>
           </NavDropdown>
        </div>

        <div className={`${styles.mobileMenu} ${mobileOpen ? styles.mobileMenuOpen : ''}`}>
          <NavLink onClick={() => setMobileOpen(false)} to="/user/home" className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}>
            Home
          </NavLink>

          <NavLink onClick={() => setMobileOpen(false)} to="/user" end className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}>
            My Learning
          </NavLink>
          <NavLink onClick={() => setMobileOpen(false)} to="/user/certificates" className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}>
            Certificates
          </NavLink>
          <NavLink onClick={() => setMobileOpen(false)} to="/playground" className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}>
            Playground
          </NavLink>
          <NavLink onClick={() => setMobileOpen(false)} to="/user/career-paths" className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}>
            Career Paths
          </NavLink>
        </div>

        {/* Change Password Modal */}
        <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title className="h5 fw-bold text-dark">Change Password</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {passwordStatus.message && (
              <Alert variant={passwordStatus.type as any} className="small py-2 mb-3">
                {passwordStatus.message}
              </Alert>
            )}
            <Form onSubmit={handleChangePassword}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-muted">Current Password</Form.Label>
                <Form.Control
                  type="password"
                  required
                  placeholder="Enter current password"
                  value={passwordForm.oldPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                  className="bg-light border-0"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-muted">New Password</Form.Label>
                <Form.Control
                  type="password"
                  required
                  placeholder="Enter new password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="bg-light border-0"
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label className="small fw-semibold text-muted">Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  required
                  placeholder="Confirm new password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="bg-light border-0"
                />
              </Form.Group>
              <Button
                type="submit"
                className="w-100 fw-bold py-2"
                style={{ background: '#F97316', borderColor: '#F97316' }}
                disabled={passwordStatus.loading}
              >
                {passwordStatus.loading ? 'Updating...' : 'Update Password'}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </header>
  );
}
